import React, { useCallback, useEffect } from 'react';
import { useATBRecs } from 'hooks/useATBRecs';
import AddToBasketDialog from 'components/AddToBasket/AddToBasketDialog/AddToBasketDialog';
import DigitalDownloadDialog from 'components/DigitalDownloadDialog/DigitalDownloadDialog';
import { useSelectedSkuContext } from 'context/SelectedSku/SelectedSkuContext';
import { CheckItsRightDialog } from 'components/Insurance/InsuranceDialog/CheckItsRightDialog';
import { InsuranceOffer } from 'types/Product';
import recsStyles from 'components/AddToBasket/AddToBasketRecommendations/AddToBasketRecommendations.module.css';
import styles from './DialogService.module.css';

const addToBasketRecsContainerId = 'recs-atb-container';

type Props = {
  hasBetterTogetherRecs?: boolean;
};

export function DialogService({ hasBetterTogetherRecs = false }: Props) {
  const {
    selectedSku,
    setSelectedInsurance,
    showCheckItsRightDialog,
    setCheckItsRightDialog,
    isAddToBasketDialogOpen,
    closeBasketDialog,
    handleContinueShopping,
    handleGoToBasket,
    recsDialogRef,
    recsAtbRef,
    isAddToBasketDialogRendered,
    isDigitalDownloadDialogRendered,
    handleDigitalDownloadAddToBasket,
    setIsDigitalDownloadDialogOpen,
    isDigitalDownloadDialogOpen,
    selectedInsurance,
    isCheckItsRightDialogRendered,
    selectedSkuInsuranceContent,
    showFindOutMore,
    setFindOutMore,
  } = useSelectedSkuContext();

  const hasATBRecsCarousel = useATBRecs(recsAtbRef, !hasBetterTogetherRecs);

  const insuranceTitle = selectedSku?.insuranceOffers && selectedSku?.insuranceOffers[0]?.offer?.productDescription;
  const handleInsuranceOptionSelect = (option: InsuranceOffer | string | null) => {
    setSelectedInsurance(option);
    if (!showCheckItsRightDialog) {
      setCheckItsRightDialog(true);
    }
  };

  const handleNoInsuranceSelect = useCallback(() => {
    setTimeout(() => setSelectedInsurance('No insurance'), 200);
  }, []);

  const handleContinueWithoutInsurance = () => {
    handleNoInsuranceSelect();
    if (showFindOutMore) {
      setFindOutMore(false);
    }
    setCheckItsRightDialog(false);
  };

  const handleCheckItsRightClose = () => {
    setTimeout(() => {
      if (!showFindOutMore) {
        setSelectedInsurance(null);
      }
      setFindOutMore(false);
    }, 200);
    setCheckItsRightDialog(false);
  };

  const handleAddInsuranceButton = () => {
    if (showFindOutMore) {
      setFindOutMore(false);
    }
    setCheckItsRightDialog(false);
  };

  useEffect(() => {
    if (
      isAddToBasketDialogOpen &&
      recsAtbRef &&
      'current' in recsAtbRef &&
      recsAtbRef.current &&
      recsDialogRef &&
      'current' in recsDialogRef &&
      recsDialogRef.current
    ) {
      recsDialogRef.current.appendChild(recsAtbRef.current);
      recsAtbRef.current.removeAttribute('hidden');
    }
  }, [isAddToBasketDialogOpen]);

  return (
    <>
      {selectedSku ? (
        <div className={styles.dialogServiceContainer}>
          {isCheckItsRightDialogRendered && insuranceTitle && selectedSkuInsuranceContent && (
            <CheckItsRightDialog
              title={insuranceTitle}
              options={selectedSku.insuranceOffers}
              onClose={handleCheckItsRightClose}
              selectedInsurance={selectedInsurance}
              open={showCheckItsRightDialog}
              handleSelectedInsurance={handleInsuranceOptionSelect}
              selectedSkuInsuranceContent={selectedSkuInsuranceContent}
              findOutMoreView={showFindOutMore}
              handleContinueWithoutInsurance={handleContinueWithoutInsurance}
              onAddInsuranceClick={handleAddInsuranceButton}
              selectedSku={selectedSku}
            />
          )}
          {isDigitalDownloadDialogRendered && (
            <DigitalDownloadDialog
              onClick={handleDigitalDownloadAddToBasket}
              onClose={() => setIsDigitalDownloadDialogOpen(false)}
              open={isDigitalDownloadDialogOpen}
            />
          )}
          {isAddToBasketDialogRendered && (
            <AddToBasketDialog
              open={isAddToBasketDialogOpen}
              onClose={closeBasketDialog}
              handleContinueShopping={handleContinueShopping}
              handleGoToBasket={handleGoToBasket}
              ref={recsDialogRef}
            />
          )}
        </div>
      ) : null}
      {!hasBetterTogetherRecs && (
        <div>
          <div
            ref={recsAtbRef}
            className={[
              recsStyles.addToBasketRecsContainer,
              hasATBRecsCarousel ? recsStyles.addToBasketRecsContainerHasCarousel : undefined,
            ]
              .filter(Boolean)
              .join(' ')}
            id={addToBasketRecsContainerId}
            data-testid="recs-atb"
            hidden
          />
        </div>
      )}
    </>
  );
}

export default DialogService;
